<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Replay</span>
      <h1>Build Your Own Steam Replay Anytime You Want</h1>
      <p>
        Official Steam Replay recaps drop once a year. steamplayti.me lets you spin up a personalized replay
        whenever you feel like celebrating wins, backlog breakthroughs, or co-op marathons.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Create your replay dashboard</router-link>
        <a class="seo-secondary-link" href="#replay-highlights">Replay highlights →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">🎬</span>
          <div>Chronological timelines spotlight the games that defined your month, quarter, or year.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🏆</span>
          <div>Surface signature genres, longest streaks, and surprise standouts with a couple clicks.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🔁</span>
          <div>Generate a Steam replay recap whenever you want progress motivation, not just every December.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="replay-highlights">
      <h2>Replay Highlights That Feel Personal</h2>
      <p>
        Because we track playtime daily, we can craft a living Steam replay that updates on demand.
        No waiting for seasonal drops or static infographics.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Genre Mix</h3>
          <p>See which genres dominated and which ones you barely touched, complete with percentage breakdowns.</p>
        </div>
        <div class="seo-card">
          <h3>Co-op Memories</h3>
          <p>Flag multiplayer-heavy sessions so you remember who joined you for late-night raids or cozy farm sims.</p>
        </div>
        <div class="seo-card">
          <h3>Milestone Moments</h3>
          <p>Call out your longest day, biggest binge, or newfound obsession for easy sharing.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>How to Build a Steam Replay with steamplayti.me</h2>
      <p>
        Drop your Steam username, switch game details to public, and let the tracker collect stats. Within minutes,
        you have enough data to frame a replay and compare it whenever you return.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Setup time</div>
          <div class="value">Under 1 min</div>
        </div>
        <div class="seo-stat">
          <div class="label">Replay refresh</div>
          <div class="value">On demand</div>
        </div>
        <div class="seo-stat">
          <div class="label">Share format</div>
          <div class="value">Public URL</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Replay Questions</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Is this official Steam Replay?</h3>
          <p>No, this is an independent tracker that mimics the insights you love from the annual recap — but you can refresh it all year.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I download graphics?</h3>
          <p>You can screenshot charts or export data directly from the dashboard to design your own highlight reels.</p>
        </div>
        <div class="seo-faq-item">
          <h3>What about private profiles?</h3>
          <p>Steam only shares playtime with public profiles. Switch back to private whenever you are done capturing your replay.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-replay" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Create a personalized Steam Replay whenever you want. Daily tracking, milestone callouts, and shareable recaps powered by steamplayti.me.';

const META = {
  title: 'Steam Replay Generator & Recap Companion | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'Steam replay, Steam recap, Steam year in review, create Steam replay, Steam replay alternative',
  canonical: 'https://steamplayti.me/page/steam-replay/',
  ogTitle: 'Steam Replay Generator & Recap Companion',
  ogDescription: 'Spin up Steam Replay style summaries throughout the year with live playtime data from steamplayti.me.',
  ogUrl: 'https://steamplayti.me/page/steam-replay/',
  twitterTitle: 'Steam Replay Generator & Recap Companion',
  twitterDescription: 'Refresh a Steam Replay recap whenever you want using automated stats from steamplayti.me.',
  structuredData: pageStructuredData({
    name: 'Steam Replay',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-replay/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Replay', path: 'https://steamplayti.me/page/steam-replay/' },
    ],
  }),
};

export default {
  name: 'SteamReplayPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
